import { PATH_DASHBOARD } from 'routes/paths'
import { ORGANIZER_APP_URL } from 'constants/index'
import { GrantedActions } from '~/services/team/interface'
import { menuRoleMapping } from 'helpers/user-access'
import {
  canAccessTeamManagement,
  filterNavByTeamRole,
} from 'helpers/team-access'
import navConfig from '~/layouts/dashboard/navbar/NavConfig'

// Synthesised by useSession, never returned by /me: Product Summary is opt-in
// per creator by env config, so there is no BE action to gate it with. Modelling
// it as an action anyway means the existing nav filter and route guard cover it,
// with no separate flag threaded through either.
export const PRODUCT_SUMMARY_ACTION = 'product_summary:view'

// Maps a nav item's path to the /me action that grants read access to it.
// Action keys must match GET /user/hub/v1/me exactly — an unknown key is never
// granted, which hides the entry from everyone, owners included.
//
// A path absent from this map is NOT gated (stays always-visible). Summary is
// the exception — it has no /me action either, but is gated on a synthesised one
// (see PRODUCT_SUMMARY_ACTION). Coin Transfer, My Promoter, My Community and
// Promoter Program used to sit in that ungated set; they are retired now and
// answer 404 through RETIRED_ROUTES below, which decides before any of this.
//
// Subscription is absent because it is retired (see RETIRED_ROUTES), not because
// it is ungated.
//
// /me also returns the product_detail:* keys, which gate in-page concerns
// (product-detail tabs) rather than any nav entry.
export const NAV_VIEW_ACTION_BY_PATH: Record<string, string> = {
  [PATH_DASHBOARD.creator_center.dashboard]: 'dashboard:view',
  [PATH_DASHBOARD.creator_center.my_content]: 'product:view',
  // /dashboard/revenue-dashboard renders the Sales Recap screen.
  [PATH_DASHBOARD.creator_center.revenue]: 'sale:view',
  [PATH_DASHBOARD.creator_center.team]: 'team:view',
  [`${ORGANIZER_APP_URL}/organization`]: 'event_checkin:view',
  [PATH_DASHBOARD.promotion.voucher]: 'voucher:view',
  [PATH_DASHBOARD.general.balance.root]: 'balance:view',
  [PATH_DASHBOARD.general.verification.root]: 'kyc:view',
  [PATH_DASHBOARD.creator_center.summary]: PRODUCT_SUMMARY_ACTION,
}

// Features that have been retired. These answer 404, not 403: the page is gone,
// not withheld — a permission screen would imply someone could grant access.
// Matched by prefix so sub-routes go with their parent. The page files still
// exist; this list is the only thing taking them out of service.
export const RETIRED_ROUTES: string[] = [
  PATH_DASHBOARD.creator_center.transfer_coin.root,
  PATH_DASHBOARD.general.verification.coin,
  PATH_DASHBOARD.promotion.my_promoter,
  PATH_DASHBOARD.general.my_community.root,
  PATH_DASHBOARD.general.promoter_program.root,
  // Also covers /subscription/create and /subscription/edit.
  PATH_DASHBOARD.creator_center.subscription,
  // Also covers /create-content/[content_uid], the editor for existing
  // digital products.
  PATH_DASHBOARD.creator_center.create_content,
]

export const isRetiredRoute = (pathname: string): boolean =>
  RETIRED_ROUTES.some(
    (path) =>
      // Guard against /dashboard/my-promoter matching /dashboard/my-promoter-x.
      pathname === path || pathname.startsWith(`${path}/`),
  )

// Routes that stay reachable without their action and render their own empty
// state instead of a 403. Every login lands on the dashboard
// (getRedirectUrl -> creator_center.dashboard), so 403-ing it would lock a
// member out of the app entirely. Its nav entry is still hidden.
const ROUTES_HANDLING_THEIR_OWN_DENIAL: string[] = [
  PATH_DASHBOARD.creator_center.dashboard,
]

const canViewPath = (permissions: GrantedActions, path: string): boolean => {
  const action = NAV_VIEW_ACTION_BY_PATH[path]
  if (!action) return true
  return permissions.includes(action)
}

// The action gating a *route*, for guarding the page itself rather than the nav
// entry. Matched by longest prefix so sub-routes inherit their parent's gate —
// /dashboard/balance/withdraw needs balance:view, rather than being left wide
// open. Returns '' when nothing matches, i.e. the route isn't gated.
export const resolveViewActionForRoute = (pathname: string): string => {
  if (!pathname) return ''

  return (
    Object.keys(NAV_VIEW_ACTION_BY_PATH)
      .filter(
        (path) =>
          // Guard against /dashboard/balance matching /dashboard/balance-foo.
          pathname === path || pathname.startsWith(`${path}/`),
      )
      .sort((a, b) => b.length - a.length)
      .map((path) => NAV_VIEW_ACTION_BY_PATH[path])[0] ?? ''
  )
}

// Hide nav items the current user has no view access to, driven entirely by
// the /me permissions (replaces the old roles/PROTECTED_PATH-based filter).
export const filterNavByPermissions = (
  menu: typeof navConfig,
  permissions: GrantedActions,
) =>
  menu
    .map((main) => ({
      ...main,
      items: main.items
        .map((item) => ({
          ...item,
          children: item.children?.filter((child) =>
            canViewPath(permissions, child.path),
          ),
        }))
        .filter(
          (item) =>
            (item.children !== undefined && item.children.length > 0) ||
            (item.children === undefined &&
              canViewPath(permissions, item.path)),
        ),
    }))
    .filter((main) => main.items !== undefined && main.items.length > 0)

export interface RouteAccessInput {
  pathname: string
  permissions: GrantedActions
  isLoadingPermissions: boolean
  isMultipleRolesEnabled: boolean
  // Result of the legacy PROTECTED_PATH check. Passed in because IsAccessible
  // is hook-shaped (it calls useRouter), and this decider stays pure.
  legacyIsAllowed: boolean
}

// Whether the current route may be opened at all. Hiding a nav entry isn't
// access control on its own; without this, typing /dashboard/balance/withdraw
// still loads the page.
export const resolveRouteAccess = ({
  pathname,
  permissions,
  isLoadingPermissions,
  isMultipleRolesEnabled,
  legacyIsAllowed,
}: RouteAccessInput): { isAllowed: boolean; isLoading: boolean } => {
  if (!isMultipleRolesEnabled) {
    return { isAllowed: legacyIsAllowed, isLoading: false }
  }

  const action = resolveViewActionForRoute(pathname)
  // Routes with no mapped action aren't permission-gated.
  if (!action) return { isAllowed: true, isLoading: false }

  if (ROUTES_HANDLING_THEIR_OWN_DENIAL.includes(pathname)) {
    return { isAllowed: true, isLoading: false }
  }

  // Keyed on the query's own loading state, not on the list being empty: a
  // member granted nothing is a resolved answer and must get a 403, not a
  // permanent spinner.
  if (isLoadingPermissions) {
    return { isAllowed: false, isLoading: true }
  }

  return { isAllowed: permissions.includes(action), isLoading: false }
}

export interface NavMenuInput {
  permissions: GrantedActions
  roles: string[]
  role: string
  isMultipleRolesEnabled: boolean
}

// Which nav entries the current user may see, plus whether they may create a
// product (the sidebar's primary CTA). Shared by both navbars so the two can't
// drift. Flag OFF keeps the legacy creator-account whitelist plus the
// Owner/Admin-only Team Management entry.
// Return type is inferred, not annotated: the filtered menu is a structurally
// widened version of navConfig, and pinning it to `typeof navConfig` makes it
// unassignable to the NavSection components' props.
export const resolveNavMenu = ({
  permissions,
  roles,
  role,
  isMultipleRolesEnabled,
}: NavMenuInput) => {
  if (isMultipleRolesEnabled) {
    return {
      menuItem: filterNavByPermissions(navConfig, permissions),
      canCreateProduct: permissions.includes('product:create'),
    }
  }

  return {
    menuItem: filterNavByTeamRole(
      menuRoleMapping(roles, navConfig),
      canAccessTeamManagement(role),
    ),
    canCreateProduct: Boolean(roles?.includes('create-content')),
  }
}
